import React from 'react';
import { Card, CardContent, Typography, Chip, Box, IconButton, useTheme } from '@mui/material';
import { LocationOn, Phone, Email, Edit, Delete, Visibility } from '@mui/icons-material';
import { Facility } from '../types/facility.types';

interface FacilityCardProps {
  facility: Facility;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onView?: (id: string) => void;
}

export const FacilityCard: React.FC<FacilityCardProps> = ({ 
  facility, 
  onEdit,
  onDelete,
  onView
}) => {
  const theme = useTheme();

  return (
    <Card 
      elevation={2}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" noWrap sx={{ fontWeight: 600 }}>
            {facility.name}
          </Typography>
          <Chip 
            label={facility.isActive ? 'Active' : 'Inactive'} 
            color={facility.isActive ? 'success' : 'default'}
            size="small"
            sx={{ ml: 1, flexShrink: 0 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
          <LocationOn color="inherit" fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" noWrap>
            {[facility.city, facility.state, facility.country].filter(Boolean).join(', ')}
          </Typography>
        </Box>

        {facility.hospital?.name && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Hospital:</strong> {facility.hospital.name}
          </Typography>
        )}

        <Box sx={{ mt: 'auto', pt: 1 }}>
          {facility.phoneNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {facility.phoneNumber}
              </Typography>
            </Box>
          )}
          
          {facility.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {facility.email}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            {facility.type && (
              <Chip 
                label={facility.type} 
                variant="outlined" 
                size="small" 
                sx={{ mr: 1 }}
              />
            )}
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {onView && (
                <IconButton 
                  size="small" 
                  onClick={() => onView(facility.id)}
                  aria-label="View facility"
                  color="primary"
                >
                  <Visibility fontSize="small" />
                </IconButton>
              )}
              {onEdit && (
                <IconButton 
                  size="small" 
                  onClick={() => onEdit(facility.id)}
                  aria-label="Edit facility"
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
              {onDelete && (
                <IconButton 
                  size="small" 
                  onClick={() => onDelete(facility.id)}
                  aria-label="Delete facility"
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
